'use client';

import {useEffect, useRef} from 'react';
import {useQuery} from '@tanstack/react-query';
import {Dialog, Modal} from 'react-aria-components';
import {arrowLeftLine, arrowRightLine, closeLine} from 'src/utils/icons';
import {getShadowColorFromBlur} from 'src/utils/getColorFromBlur';
import useUnsplashBeacons from 'src/hooks/useUnsplashBeacons';
import RoundedCornersFilter from './RoundedCornersFilter';
import getImageAltText from 'src/utils/getImageAltText';
import ImageDescription from '../ImageDescription';
import getImageUrl from 'src/utils/getImageUrl';
import FadingImage from '../FadingImage';
import BaseButton from '../BaseButton';
import ImageWall from './ImageWall';

type Props = {
  onClose: () => void;
  initialImages: PlaceImage[];
  fetchImages?: () => Promise<PlaceImage[]>;
  activeImageId: string;
  setActiveImageId: (imageId: string, placeId?: string) => void;
  placeId: string;
  placeName: string;
  countryName: string;
};

const ImageGalleryModal = ({
  onClose,
  initialImages,
  activeImageId,
  setActiveImageId,
  fetchImages,
  placeId,
  placeName,
  countryName
}: Props) => {
  const scrollContentRef = useRef<HTMLDivElement>(null);

  const {data: images = []} = useQuery<PlaceImage[]>({
    queryKey: ['galleryImages', placeId],
    queryFn: fetchImages,
    enabled: !!fetchImages,
    placeholderData: initialImages
  });

  const activeImage = images.find((image) => image._id === activeImageId);
  const imageIndex = images.findIndex((image) => image._id === activeImageId);
  const galleryImages = [...images.slice(imageIndex + 1), ...images.slice(0, imageIndex)];

  useUnsplashBeacons(activeImage ? [activeImage] : []);

  const shadowColor = getShadowColorFromBlur(activeImage?.blurHash);
  const isShadowBlack = shadowColor.every((color) => color === 0);

  const goPrevious = () => {
    if (imageIndex === 0 || imageIndex === -1) return;

    setActiveImageId(images[imageIndex - 1]._id, placeId);
  };

  const goNext = () => {
    if (imageIndex === images.length - 1 || imageIndex === -1) return;

    setActiveImageId(images[imageIndex + 1]._id, placeId);
  };

  useEffect(() => {
    scrollContentRef?.current?.scrollTo({top: 0});

    const listener = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goPrevious();
      if (e.key === 'ArrowRight') goNext();
    };

    window.addEventListener('keyup', listener);

    return () => window.removeEventListener('keyup', listener);
  }, [imageIndex]);

  return (
    <>
      <Modal
        isOpen
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        className="bg-(--page-background) fixed left-0 top-0 bottom-0 right-0 z-20 will-change-transform animate-gallery-appear"
      >
        <Dialog aria-label="Image gallery">
          <div
            className="absolute top-0 left-0 right-0 bottom-0 opacity-20 transition-colors duration-500"
            style={{backgroundColor: isShadowBlack ? 'rgba(0,0,0,0)' : `rgba(${shadowColor.join(',')})`}}
          />

          <div className="absolute left-0 top-0 bottom-0 right-0 overflow-y-auto pb-20" ref={scrollContentRef}>
            <BaseButton
              icon={closeLine}
              iconSize={22}
              buttonVariant="translucent"
              className="fixed right-4 top-4 w-[42px] z-10"
              onPress={onClose}
            />

            <RoundedCornersFilter filterId="filter-radius" />

            <div className="flex flex-col px-[72px] pt-16 w-full h-[calc(100vh-140px)] [@media(max-height:700px)]:h-[calc(100%-50px)] max-[800px]:px-(--page-padding) max-[500px]:px-0">
              <BaseButton
                onPress={goPrevious}
                icon={arrowLeftLine}
                iconSize={22}
                buttonVariant="secondary"
                className="absolute left-4 top-[calc(50%-50px)] w-[42px] max-[800px]:hidden"
              />

              <BaseButton
                onPress={goNext}
                icon={arrowRightLine}
                iconSize={22}
                buttonVariant="secondary"
                className="absolute right-4 top-[calc(50%-50px)] w-[42px] max-[800px]:hidden"
              />

              {activeImage ? (
                <div className="relative grow">
                  <FadingImage
                    key={activeImage._id}
                    className="object-contain"
                    style={{filter: 'url(#filter-radius)'}}
                    fill
                    priority
                    src={getImageUrl(activeImage.path)}
                    alt={getImageAltText(activeImage, placeName, countryName)}
                    sizes="100vw"
                  />
                </div>
              ) : null}
            </div>

            <div className="pt-6 pb-10 px-(--page-padding) flex items-center justify-center">
              {activeImage ? <ImageDescription image={activeImage} /> : null}
            </div>

            <ImageWall
              placeId={placeId}
              placeName={placeName}
              countryName={countryName}
              images={galleryImages}
              className="px-[72px] max-[1200px]:px-(--page-padding) max-[500px]:px-0"
              setActiveImageId={setActiveImageId}
            />
          </div>
        </Dialog>
      </Modal>
    </>
  );
};

export default ImageGalleryModal;
